/**
 * RFC tab — demo-styled stage tracker for an account's Request for Coverage.
 *
 * Mirrors the synrgyinfinity RfcView look-and-feel: a status banner, a header
 * with the RFC title + status pill + PDF/Print actions, a sub-tab nav
 * (Submission Details / Files / Notes / History), a completion progress bar +
 * jump-to-section strip, a demo-styled access-link panel, and collapsible
 * section cards with colored icon chips. The fields are populated from the real
 * RFC data (section125 / MEC forms); only the chrome matches the demo.
 *
 * Scope: this restyle is intentionally limited to the Accounts RFC tab. The
 * deal/prospect RFC pages (ClientRfcReview, ProspectDetail) are untouched.
 */

import React from 'react';
import {useForm, FormProvider} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  Banknote,
  Building2,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Copy,
  DollarSign,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Link2,
  MapPin,
  MessageSquare,
  Pencil,
  Printer,
  RefreshCcw,
  Save,
  ShieldCheck,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Textarea} from '@common-web/components/ui/textarea';
import {cn} from '@common-web/lib/utils';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';
import {useAuthStore} from '@common-web/stores/authStore';
import {RfcStatus, RfcStep, RfcAttachmentType, RfcAction} from '@common/types/rfc';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useClientActionAllowed, useHasPermission} from '@common-web/hooks/use-permissions';
import type {LifecyclePhase, AccountsSubStage, AccountsArchiveReason} from '@api/generated/client';
import {
  SUB_STAGE_LABEL,
  SUB_STAGE_STATUS_COLORS,
  toActiveSubStage,
} from '@common-web/components/account/phaseLabels';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {
  findPositionBySystemSubStage,
  resolveAccountPosition,
  type AccountsViewConfig,
} from '@common/accountsView';
import {AssignedBrokerLink} from '@common-web/components/account/AssignedBrokerLink';
import {RfcGeneratePdfButton} from '@common-web/components/rfc/RfcPdfExport';
import {RfcFileList} from '@common-web/components/rfc/RfcFileList';
import {RfcHistory} from '@common-web/components/rfc/RfcHistory';
import {RfcStepForm} from '@client/pages/rfc/forms/RfcStepForm';
import {RfcContext} from '@client/pages/rfc/RfcContext';
import {achFormHasData} from '@common/intakePackage';
import {
  getBlankRfcFormState,
  rfcSection125FormDraftSchema,
  rfcMecFormDraftSchema,
  rfcClientPortalAccessFormDraftSchema,
  buildAgentOfRecordFromBroker,
} from '@common/lib/rfcSchema';
import type {
  RfcAchForm,
  RfcClientPortalAccessForm,
  RfcMecForm,
  RfcSection125Form,
} from '@common/lib/rfcSchema';

// Mirrors AdminRfcEditor's form schema so inline section edits validate against
// the same draft rules the wizard/deal editor use.
const wizardFormSchema = z.object({
  section125Form: rfcSection125FormDraftSchema,
  mecForm: rfcMecFormDraftSchema,
  clientPortalAccessForm: rfcClientPortalAccessFormDraftSchema,
});
type WizardFormData = z.infer<typeof wizardFormSchema>;

interface RfcTabProps {
  accountsId: string;
}

/* ── Demo palette (synrgyinfinity index.css tokens) ───────────────────────── */
const INK_500 = '#64748B';

type StatusMeta = {label: string; bg: string; text: string};

const ARCHIVED_META: Record<string, StatusMeta> = {
  REJECTED: {label: 'Lost', bg: '#FEF2F2', text: '#DC2626'},
  ON_HOLD: {label: 'On Hold', bg: '#FEF3C7', text: '#D97706'},
};

// The banner and status pill follow the account's displayed sub-stage — the
// same node every other Accounts surface shows — rather than the legacy
// RfcSubmission.status, so they always agree with the lifecycle band. (The
// accounts-view approval actions now transition the legacy status too, via
// the shared lib/rfcAdjudication helpers, but the account remains the single
// display source of truth here.)
function accountStatusMeta(
  config: AccountsViewConfig,
  account: {
    lifecyclePhase: LifecyclePhase;
    subStage: AccountsSubStage;
    viewStageKey?: string | null;
    viewSubStageKey?: string | null;
  },
  isArchived: boolean,
  archiveReason: AccountsArchiveReason | null,
): StatusMeta {
  if (isArchived) {
    return ARCHIVED_META[archiveReason ?? ''] ?? {label: 'Archived', bg: '#F1F5F9', text: INK_500};
  }
  // Mirror the Accounts table / detail header badge exactly: advance to the
  // active milestone, but don't advance onto a milestone this tenant's pipeline
  // omits — fall back to the account's own represented sub-stage so the label
  // matches the config (and the colors stay keyed off the displayed sub-stage).
  const position = resolveAccountPosition(config, account);
  const activeSubStage = toActiveSubStage(account.subStage);
  const activePosition =
    activeSubStage === account.subStage
      ? position
      : findPositionBySystemSubStage(config, activeSubStage);
  const displaySubStage = activePosition ? activeSubStage : account.subStage;
  const displayPosition = activePosition ?? position;
  const label = displayPosition?.subStage.label ?? SUB_STAGE_LABEL[displaySubStage];
  return {label, ...SUB_STAGE_STATUS_COLORS[displaySubStage]};
}

/* ── Shared field primitives (demo styling) ───────────────────────────────── */
function Field({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value: unknown;
  fullWidth?: boolean;
}): React.ReactElement {
  const display =
    value === undefined || value === null || value === ''
      ? '—'
      : typeof value === 'boolean'
        ? value
          ? 'Yes'
          : 'No'
        : String(value);
  const empty = display === '—';
  return (
    <div className={fullWidth ? 'col-span-2' : ''}>
      <p className='mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-[#94A3B8]'>
        {label}
      </p>
      <p className={cn('text-[15px] font-medium', empty ? 'text-[#94A3B8]' : 'text-[#0F172A]')}>
        {display}
      </p>
    </div>
  );
}

type RfcContact =
  | {firstName?: string; lastName?: string; title?: string; phone?: string; email?: string}
  | undefined;

function ContactBlock({label, contact}: {label: string; contact: RfcContact}): React.ReactElement {
  return (
    <div className='mb-4 rounded-lg border border-[#F1F5F9] bg-[#FAFBFC] p-5 last:mb-0'>
      <p className='mb-4 text-[14px] font-bold text-[#0F172A]'>{label}</p>
      <div className='grid grid-cols-2 gap-x-8 gap-y-4'>
        <Field label='First Name' value={contact?.firstName} />
        <Field label='Last Name' value={contact?.lastName} />
        <Field label='Title' value={contact?.title} />
        <Field label='Email' value={contact?.email} />
        <Field label='Phone' value={contact?.phone} />
      </div>
    </div>
  );
}

type RfcLocationLike = {
  locationName?: string;
  fein?: string;
  payrollLocationCode?: string;
  companyAddress?: {street?: string; city?: string; state?: string; zip?: string};
  achForm?: RfcAchForm;
};

type RfcAdminLike = (RfcSection125Form | RfcMecForm)['admin'];

// A freshly-generated RFC is seeded from getBlankRfcFormState(), which already
// contains one blank location — the Locations check has to detect real user
// input rather than mere presence.
function isMeaningfulLocation(loc: RfcLocationLike | undefined): boolean {
  const addr = loc?.companyAddress;
  return (
    !!loc?.locationName?.trim() ||
    !!loc?.fein?.trim() ||
    !!loc?.payrollLocationCode?.trim() ||
    !!addr?.street?.trim() ||
    !!addr?.city?.trim() ||
    !!addr?.state?.trim() ||
    !!addr?.zip?.trim()
  );
}

function hasAgentInput(agent: RfcSection125Form['agentOfRecord'] | undefined): boolean {
  return !!(agent?.firstName?.trim() || agent?.lastName?.trim() || agent?.email?.trim());
}

// The blank template's admin defaults (full/part-time eligible, bi-weekly
// payroll) are themselves valid, submittable answers, so the section counts as
// complete once its required fields hold values. Waiting period and enrollment
// company are optional on the submission schema and deliberately do not gate
// completion.
function isMeaningfulAdmin(admin: RfcAdminLike | undefined): boolean {
  if (!admin) return false;
  return (
    (admin.eligibility?.fullTime === true || admin.eligibility?.partTime === true) &&
    (admin.payrollFrequencies ?? []).length > 0
  );
}

// Payroll is fully optional on the submission schema, so the section counts as
// complete once it holds any real input rather than gating on a specific field.
function hasPayrollInput(
  payrollInfo: (RfcSection125Form | RfcMecForm)['payrollInfo'] | undefined,
): boolean {
  if (!payrollInfo) return false;
  const hasContactInput = (contact: RfcContact) =>
    !!(
      contact?.firstName?.trim() ||
      contact?.lastName?.trim() ||
      contact?.phone?.trim() ||
      contact?.email?.trim()
    );
  return !!(
    payrollInfo.vendor?.trim() ||
    payrollInfo.system?.trim() ||
    payrollInfo.payrollCompanyId?.trim() ||
    hasContactInput(payrollInfo.contact) ||
    hasContactInput(payrollInfo.payrollVendorContact)
  );
}

// Banking identifiers are sensitive — only ever surface the last 4 digits in
// the read view, with the remainder masked. Empty values fall through to the
// Field's own '—' placeholder.
function maskBankingIdentifier(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const last4 = trimmed.slice(-4);
  const maskedLen = Math.max(trimmed.length - last4.length, 0);
  return `${'•'.repeat(maskedLen)}${last4}`;
}

function AchBlock({achForm}: {achForm: RfcAchForm}): React.ReactElement {
  const acct = achForm.bankAccount;
  const details = achForm.achDetails;
  const auth = achForm.authorization;
  return (
    <div className='mt-4 border-t border-[#F1F5F9] pt-4'>
      <div className='mb-3 flex items-center gap-2'>
        <Banknote size={14} className='text-[#3B82F6]' />
        <p className='text-[14px] font-bold text-[#0F172A]'>ACH / Banking</p>
      </div>
      <div className='grid grid-cols-2 gap-x-8 gap-y-4'>
        <Field label='Business Name on Account' value={acct?.businessNameOnAccount} />
        <Field label='Account Holder First Name' value={acct?.accountHolderFirstName} />
        <Field label='Account Holder Last Name' value={acct?.accountHolderLastName} />
        <Field label='Contact Phone' value={acct?.contactPhone} />
        <Field label='Contact Email' value={acct?.contactEmail} />
        <Field label='Account Street' value={acct?.address?.street} />
        <Field label='Account City' value={acct?.address?.city} />
        <Field label='Account State' value={acct?.address?.state} />
        <Field label='Account ZIP' value={acct?.address?.zip} />
        <Field label='Bank Name' value={details?.bankName} />
        <Field label='Routing Number' value={maskBankingIdentifier(details?.routingNumber)} />
        <Field label='Account Number' value={maskBankingIdentifier(details?.accountNumber)} />
        <Field label='Bank Street' value={details?.bankAddress?.street} />
        <Field label='Bank City' value={details?.bankAddress?.city} />
        <Field label='Bank State' value={details?.bankAddress?.state} />
        <Field label='Bank ZIP' value={details?.bankAddress?.zip} />
        <Field label='Authorized Signature' value={auth?.authorizedSignature} />
        <Field label='Signature Date' value={auth?.signatureDate} />
      </div>
    </div>
  );
}

function LocationBlock({loc, idx}: {loc: RfcLocationLike; idx: number}): React.ReactElement {
  const addr = loc.companyAddress;
  return (
    <div className='mb-4 rounded-lg border border-[#F1F5F9] bg-[#FAFBFC] p-5 last:mb-0'>
      <div className='mb-4 flex items-center gap-2'>
        <MapPin size={14} className='text-[#14B8A6]' />
        <p className='text-[14px] font-bold text-[#0F172A]'>
          {loc.locationName || `Location ${idx + 1}`}
        </p>
      </div>
      <div className='grid grid-cols-2 gap-x-8 gap-y-4'>
        <Field label='FEIN' value={loc.fein} />
        <Field label='Payroll Location Code' value={loc.payrollLocationCode} />
        <Field label='Street' value={addr?.street} />
        <Field label='City' value={addr?.city} />
        <Field label='State' value={addr?.state} />
        <Field label='ZIP' value={addr?.zip} />
      </div>
      {/* ACH banking is captured per-location and is intentionally shown to
          brokers and internal/client admins regardless of the tenant
          `rfcAchAuthorization` flag — disabling the flag only hides the
          submitter's ACH wizard step, it does not hide already-captured
          banking details from the account detail view. */}
      {achFormHasData(loc.achForm) && loc.achForm ? <AchBlock achForm={loc.achForm} /> : null}
    </div>
  );
}

/* ── Collapsible section card (demo styling) ──────────────────────────────── */
interface SectionDescriptor {
  key: string;
  title: string;
  shortLabel: string;
  icon: React.ReactNode;
  iconBg: string;
  complete: boolean;
  body: React.ReactNode;
  /** Wizard step rendered when this card is put into edit mode. */
  step: RfcStep;
}

function SectionCard({
  section,
  collapsed,
  onToggle,
  canEdit,
  editing,
  onEdit,
  onSave,
  onCancel,
  saving,
  showPepm,
}: {
  section: SectionDescriptor;
  collapsed: boolean;
  onToggle: () => void;
  canEdit: boolean;
  editing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  showPepm: boolean;
}): React.ReactElement {
  // While editing, the card is forced open and the toggle/chevron are hidden.
  const open = editing || !collapsed;
  return (
    <div
      id={`rfc-section-${section.key}`}
      className={cn(
        'mb-5 rounded-[14px] border bg-white shadow-sm',
        editing ? 'border-[#3B82F6] ring-1 ring-[#3B82F6]/20' : 'border-[#E4E8EE]',
      )}
    >
      <div className='flex items-center justify-between px-7 py-5'>
        <button
          type='button'
          onClick={editing ? undefined : onToggle}
          disabled={editing}
          className='flex flex-1 items-center gap-3 text-left'
        >
          <div
            className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg'
            style={{backgroundColor: section.iconBg}}
          >
            {section.icon}
          </div>
          <h3 className='text-[20px] font-bold leading-tight text-[#0F172A]'>{section.title}</h3>
        </button>
        <div className='flex items-center gap-1'>
          {canEdit && !editing ? (
            <button
              type='button'
              onClick={onEdit}
              title='Edit section'
              className='flex h-8 w-8 items-center justify-center rounded-lg text-[#64748B] transition hover:bg-[#F1F5F9] hover:text-[#3B82F6]'
            >
              <Pencil size={15} />
            </button>
          ) : null}
          {!editing ? (
            <button
              type='button'
              onClick={onToggle}
              className='flex h-8 w-8 items-center justify-center rounded-lg text-[#94A3B8] transition hover:bg-[#F1F5F9]'
            >
              {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </button>
          ) : null}
        </div>
      </div>
      {open ? (
        <>
          <div className='mx-7 border-t border-[#F1F5F9]' />
          <div className='px-7 py-6'>
            {editing ? (
              <div className='space-y-6'>
                <RfcStepForm
                  currentStep={section.step}
                  disableFormCopy
                  showEnrollmentType
                  showPepm={showPepm}
                />
                <div className='flex justify-end gap-2 border-t border-[#F1F5F9] pt-4'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={onCancel}
                    disabled={saving}
                  >
                    <X className='mr-1.5 h-4 w-4' /> Cancel
                  </Button>
                  <Button type='button' size='sm' onClick={onSave} disabled={saving}>
                    <Save className='mr-1.5 h-4 w-4' />
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              </div>
            ) : (
              section.body
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

/* ── Section builders ─────────────────────────────────────────────────────── */
function programSections(
  form: RfcSection125Form | RfcMecForm,
  prefix: string,
  fallbackAgent: RfcSection125Form['agentOfRecord'] | null,
  assignedBroker: {id: string; name: string} | null,
): SectionDescriptor[] {
  const groupInfo = form.groupInfo;
  const admin = form.admin;
  const contacts = form.contacts;
  const payrollInfo = form.payrollInfo;
  // The Agent of Record defaults to the account's assigned broker: RFCs created
  // before the agent was seeded from the broker carry a blank agent section, so
  // fall back to the broker for display and completion.
  const ownAgent = form.agentOfRecord;
  const agent = hasAgentInput(ownAgent) ? ownAgent : (fallbackAgent ?? ownAgent);
  const locations = (groupInfo?.locations ?? []) as RfcLocationLike[];
  const is125 = prefix === '125';
  const steps = {
    group: is125 ? RfcStep.SECTION_125_FORM_GROUP_INFO : RfcStep.MEC_FORM_GROUP_INFO,
    admin: is125 ? RfcStep.SECTION_125_FORM_ADMIN : RfcStep.MEC_FORM_ADMIN,
    contacts: is125 ? RfcStep.SECTION_125_FORM_CONTACTS : RfcStep.MEC_FORM_CONTACTS,
    payroll: is125 ? RfcStep.SECTION_125_FORM_PAYROLL_INFO : RfcStep.MEC_FORM_PAYROLL_INFO,
    agent: is125 ? RfcStep.SECTION_125_FORM_AGENT : RfcStep.MEC_FORM_AGENT,
  };

  return [
    {
      key: `${prefix}-company`,
      title: 'Company Information',
      shortLabel: 'Company',
      icon: <Building2 size={18} className='text-[#3B82F6]' />,
      iconBg: '#DBEAFE',
      complete: !!groupInfo?.companyName,
      step: steps.group,
      body: (
        <div className='grid grid-cols-2 gap-x-8 gap-y-5'>
          <Field label='Benefit Year' value={groupInfo?.benefitYear} />
          <Field label='Company Legal Name' value={groupInfo?.companyName} />
          <Field label='Company DBA' value={groupInfo?.companyDba} />
          <Field label='Other Known Name' value={groupInfo?.otherKnownName} />
          <Field label='SIC Code' value={groupInfo?.sic} />
          <Field label='Primary State of Doing Business' value={groupInfo?.stateOfDoingBusiness} />
          <Field label='Total # of Employees' value={groupInfo?.totalEmployees} />
          <Field label='Number of Locations' value={groupInfo?.numberOfLocations} />
        </div>
      ),
    },
    {
      key: `${prefix}-locations`,
      title: 'Locations',
      shortLabel: 'Locations',
      icon: <MapPin size={18} className='text-[#14B8A6]' />,
      iconBg: '#CCFBF1',
      complete: locations.some(isMeaningfulLocation),
      step: steps.group,
      body:
        locations.length === 0 ? (
          <p className='py-6 text-center text-[14px] text-[#94A3B8]'>No locations added yet.</p>
        ) : (
          <>
            {locations.map((loc, i) => (
              <LocationBlock key={i} loc={loc} idx={i} />
            ))}
          </>
        ),
    },
    {
      key: `${prefix}-eligibility`,
      title: 'Eligibility & Administration',
      shortLabel: 'Eligibility',
      icon: <ClipboardCheck size={18} className='text-[#8B5CF6]' />,
      iconBg: '#EDE9FE',
      complete: isMeaningfulAdmin(admin),
      step: steps.admin,
      body: (
        <div className='grid grid-cols-2 gap-x-8 gap-y-5'>
          <Field label='Full-Time Employees Eligible' value={admin?.eligibility?.fullTime} />
          <Field label='Part-Time Employees Eligible' value={admin?.eligibility?.partTime} />
          <Field label='Waiting Period (days)' value={admin?.waitingPeriod} />
          <Field
            label='Payroll Frequencies'
            value={admin?.payrollFrequencies
              ?.map((x) => x.replace(/_/g, ' ').toLowerCase())
              ?.join(', ')}
          />
          <Field label='Enrollment Company' value={admin?.enrollmentCompany} />
        </div>
      ),
    },
    {
      key: `${prefix}-contacts`,
      title: 'Contacts',
      shortLabel: 'Contacts',
      icon: <Users size={18} className='text-[#F97316]' />,
      iconBg: '#FFF7ED',
      complete: !!contacts?.authorizedSigner?.firstName,
      step: steps.contacts,
      body: (
        <>
          <ContactBlock
            label='Authorized Signer (Plan Administrator)'
            contact={contacts?.authorizedSigner}
          />
          <ContactBlock label='Eligibility Contact' contact={contacts?.eligibilityContact} />
          <ContactBlock label='Billing Contact' contact={contacts?.billingContact} />
        </>
      ),
    },
    {
      key: `${prefix}-payroll`,
      title: 'Payroll Information',
      shortLabel: 'Payroll',
      icon: <DollarSign size={18} className='text-[#10B981]' />,
      iconBg: '#D1FAE5',
      complete: hasPayrollInput(payrollInfo),
      step: steps.payroll,
      body: (
        <>
          <div className='mb-4 grid grid-cols-2 gap-x-8 gap-y-5'>
            <Field label='Payroll Vendor' value={payrollInfo?.vendor} />
            <Field label='Payroll System' value={payrollInfo?.system} />
            <Field label='Payroll Company ID' value={payrollInfo?.payrollCompanyId} />
          </div>
          <ContactBlock label='Internal Payroll Contact' contact={payrollInfo?.contact} />
          <ContactBlock
            label='Payroll Vendor Contact'
            contact={payrollInfo?.payrollVendorContact}
          />
        </>
      ),
    },
    {
      key: `${prefix}-agent`,
      title: 'Agent of Record',
      shortLabel: 'Agent',
      icon: <UserCheck size={18} className='text-[#3B82F6]' />,
      iconBg: '#DBEAFE',
      complete: !!agent?.firstName,
      step: steps.agent,
      body: (
        <div className='space-y-5'>
          {assignedBroker ? (
            <div>
              <p className='mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-[#94A3B8]'>
                Assigned Broker
              </p>
              <AssignedBrokerLink broker={assignedBroker} className='text-[15px] font-medium' />
            </div>
          ) : null}
          <div className='grid grid-cols-2 gap-x-8 gap-y-5'>
            <Field label='Agent First Name' value={agent?.firstName} />
            <Field label='Agent Last Name' value={agent?.lastName} />
            <Field label='Phone' value={agent?.phone} />
            <Field label='Email' value={agent?.email} />
            <Field label='Additional Phone' value={agent?.additionalPhone} />
            <Field label='Additional Email' value={agent?.additionalEmail} />
          </div>
        </div>
      ),
    },
  ];
}

/* ── Completion strip ─────────────────────────────────────────────────────── */
function CompletionStrip({
  sections,
  onJump,
}: {
  sections: SectionDescriptor[];
  onJump: (key: string) => void;
}): React.ReactElement {
  return (
    <div className='mb-5 flex flex-wrap items-center gap-2 pb-1'>
      {sections.map((s) => (
        <button
          key={s.key}
          type='button'
          onClick={() => onJump(s.key)}
          className={cn(
            'flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[12px] font-semibold transition hover:shadow-sm',
            s.complete
              ? 'border-[#A7F3D0] bg-[#D1FAE5] text-[#059669]'
              : 'border-[#E4E8EE] bg-[#F8FAFC] text-[#94A3B8]',
          )}
        >
          {s.complete ? (
            <Check size={12} />
          ) : (
            <span className='h-3 w-3 rounded-full border-2 border-current' />
          )}
          {s.shortLabel}
        </button>
      ))}
    </div>
  );
}

/* ── Access link panel (demo styling) ─────────────────────────────────────── */
function AccessLinkPanel({
  accessUrl,
  newPassword,
  onCopyLink,
  copied,
  onRegenerate,
  regenerating,
}: {
  accessUrl: string;
  newPassword: string | null;
  onCopyLink: () => void;
  copied: boolean;
  onRegenerate: () => void;
  regenerating: boolean;
}): React.ReactElement {
  const [showPw, setShowPw] = React.useState(false);
  const [pwCopied, setPwCopied] = React.useState(false);

  const copyPassword = (): void => {
    if (!newPassword) return;
    navigator.clipboard.writeText(newPassword);
    setPwCopied(true);
    setTimeout(() => setPwCopied(false), 1500);
  };

  const IconBtn = ({
    onClick,
    title,
    children,
  }: {
    onClick: () => void;
    title: string;
    children: React.ReactNode;
  }): React.ReactElement => (
    <button
      type='button'
      onClick={onClick}
      title={title}
      className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#64748B] transition hover:bg-[#F1F5F9]'
    >
      {children}
    </button>
  );

  return (
    <div className='mb-5 rounded-[14px] border border-[#E4E8EE] bg-white p-6 shadow-sm'>
      <div className='mb-3 flex items-center gap-2'>
        <Link2 size={18} className='text-[#14B8A6]' />
        <h3 className='text-[16px] font-bold text-[#0F172A]'>RFC Access Link</h3>
      </div>
      <p className='mb-4 text-[14px] text-[#64748B]'>
        Share this link with the submitter to start the RFC. Updates sync automatically to this
        page.
      </p>

      <div className='flex flex-col gap-4'>
        <div>
          <p className='mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#94A3B8]'>
            Link
          </p>
          <div className='flex items-center gap-1.5'>
            <div
              className='flex-1 truncate rounded-lg bg-[#F5F7FA] px-3 py-2.5 font-mono text-[13px] text-[#0F172A]'
              title={accessUrl}
            >
              {accessUrl}
            </div>
            <IconBtn onClick={onCopyLink} title='Copy link'>
              {copied ? <Check size={14} className='text-[#10B981]' /> : <Copy size={14} />}
            </IconBtn>
            <IconBtn onClick={() => window.open(accessUrl, '_blank')} title='Open wizard'>
              <ExternalLink size={14} />
            </IconBtn>
          </div>
        </div>

        <div>
          <p className='mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#94A3B8]'>
            Password
          </p>
          {newPassword ? (
            <div className='flex items-center gap-1.5'>
              <div className='flex-1 rounded-lg bg-[#F5F7FA] px-3 py-2.5 font-mono text-[13px] text-[#0F172A]'>
                {showPw ? newPassword : '••••••••'}
              </div>
              <IconBtn onClick={() => setShowPw((v) => !v)} title={showPw ? 'Hide' : 'Show'}>
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </IconBtn>
              <IconBtn onClick={copyPassword} title='Copy password'>
                {pwCopied ? <Check size={14} className='text-[#10B981]' /> : <Copy size={14} />}
              </IconBtn>
            </div>
          ) : (
            <p className='text-[13px] text-[#64748B]'>
              The existing password can&apos;t be revealed — generate a new one to share with the
              submitter.
            </p>
          )}
        </div>

        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={onRegenerate}
          disabled={regenerating}
          className='w-full'
        >
          <RefreshCcw className='mr-1.5 h-3.5 w-3.5' />
          {regenerating ? 'Generating…' : newPassword ? 'Regenerate password' : 'Generate password'}
        </Button>
      </div>
    </div>
  );
}

/* ── Sub-tab nav ──────────────────────────────────────────────────────────── */
type SubTab = 'details' | 'files' | 'notes' | 'history';

export function RfcTab({accountsId}: RfcTabProps): React.ReactElement {
  const utils = trpc.useUtils();
  const query = trpc.account.getById.useQuery({accountsId});

  const [newPassword, setNewPassword] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [reviewNote, setReviewNote] = React.useState('');
  const [subTab, setSubTab] = React.useState<SubTab>('details');
  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
  // Which section card is currently in edit mode (null = all read-only). Only
  // one card edits at a time so two cards backed by the same wizard step (e.g.
  // Company + Locations both map to GROUP_INFO) never register fields twice.
  const [editingKey, setEditingKey] = React.useState<string | null>(null);

  const isBrokerUser = useAuthStore((state) => state.isBrokerUser());
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const user = useAuthStore((state) => state.user);
  // Display vs capability: `rfc.view_pepm` only controls PEPM visibility;
  // review and inline-edit controls key off `workflow.approve_rfc` so RFC
  // managers without PEPM access keep their actions and view-only roles
  // never receive action controls.
  const canViewPepm = useHasPermission(CODE_PERMISSIONS.rfc.viewPepm.key);
  const canManageRfc = useHasPermission(CODE_PERMISSIONS.workflow.approveRfc.key);
  // The inline section editors save via clients.rfc.updateFormAdmin, which
  // enforces `rfc.edit` for client-profile callers — hide the pencils for
  // roles that would only fail at save. Brokers/internal pass by profile.
  const canEditRfcForm = useClientActionAllowed(CODE_PERMISSIONS.rfc.edit.key);
  const isClientAdmin = !!user?.clientProfile && canManageRfc;
  const showPepm = isInternalUser || (!!user?.clientProfile && canViewPepm);
  const config = useAccountsViewConfig();

  const account = query.data;
  const details = account?.rfcSubmission;
  const rfcId = details?.id;

  // Files / Notes / History come from the full RFC record (the same
  // RfcSubmission this account is backed by), loaded lazily once we know it
  // exists. The deal review page reuses the same query + widgets.
  const rfcDetail = trpc.clients.rfc.getById.useQuery({rfcId: rfcId ?? ''}, {enabled: !!rfcId});

  // Shared form behind the inline section editors. Seeded from the RFC's form
  // JSON and reset whenever the RFC data changes (load, save, refetch).
  const form = useForm<WizardFormData>({
    resolver: zodResolver(wizardFormSchema),
    defaultValues: getBlankRfcFormState(),
    mode: 'onChange',
  });

  const resetForm = React.useCallback(() => {
    const blank = getBlankRfcFormState();
    const d = query.data?.rfcSubmission;
    let section125Form = (d?.section125FormData as RfcSection125Form) ?? blank.section125Form;
    let mecForm = (d?.mecFormData as RfcMecForm) ?? blank.mecForm;
    // Seed a blank Agent of Record from the account's assigned broker so the
    // inline editor starts from the same default the read view shows.
    const broker = query.data?.assignedBroker;
    if (broker) {
      const fallbackAgent = buildAgentOfRecordFromBroker(broker);
      if (!hasAgentInput(section125Form.agentOfRecord)) {
        section125Form = {...section125Form, agentOfRecord: {...fallbackAgent}};
      }
      if (!hasAgentInput(mecForm.agentOfRecord)) {
        mecForm = {...mecForm, agentOfRecord: {...fallbackAgent}};
      }
    }
    form.reset({
      section125Form,
      mecForm,
      clientPortalAccessForm:
        (d?.clientPortalAccessData as RfcClientPortalAccessForm) ?? blank.clientPortalAccessForm,
    });
  }, [form, query.data]);

  React.useEffect(() => {
    // Never reset while a section is being edited: background refetches
    // (window focus, the 10s stale timer, invalidations from other widgets)
    // produce a fresh rfcSubmission reference even when nothing changed —
    // superjson revives Dates, which defeats React Query's structural sharing —
    // and resetting mid-edit silently reverts the user's changes.
    if (editingKey !== null) return;
    if (query.data?.rfcSubmission) resetForm();
  }, [query.data?.rfcSubmission, resetForm, editingKey]);

  const updateFormAdmin = trpc.clients.rfc.updateFormAdmin.useMutation({
    onError: createMutationErrorHandler('Form Validation Error'),
  });

  const onReviewSuccess = (title: string): void => {
    setReviewNote('');
    utils.account.getById.invalidate({accountsId});
    if (rfcId) utils.clients.rfc.getById.invalidate({rfcId});
    toast({title});
  };

  const approveBrokerStep = trpc.clients.rfc.approveBrokerStep.useMutation({
    onSuccess: () => onReviewSuccess('Submission approved'),
    onError: (err) => toast({title: 'Approval failed', description: err.message}),
  });
  const brokerReject = trpc.clients.rfc.brokerReject.useMutation({
    onSuccess: () => onReviewSuccess('Submission rejected'),
    onError: (err) => toast({title: 'Rejection failed', description: err.message}),
  });
  const adjudicate = trpc.clients.rfc.adjudicate.useMutation({
    onSuccess: () => onReviewSuccess('Submission updated'),
    onError: (err) => toast({title: 'Review failed', description: err.message}),
  });

  const regeneratePassword = trpc.account.regeneratePassword.useMutation({
    onSuccess: (result) => {
      setNewPassword(result.plaintextPassword);
      utils.account.getById.invalidate({accountsId});
    },
    onError: (err) => toast({title: 'Password generation failed', description: err.message}),
  });

  // Legacy accounts created before RFCs were auto-provisioned won't have a
  // details row yet — this backfills one on demand.
  const startRfc = trpc.account.startRfc.useMutation({
    onSuccess: (result) => {
      setNewPassword(result.plaintextPassword);
      utils.account.getById.invalidate({accountsId});
    },
    onError: (err) => toast({title: 'Could not create RFC', description: err.message}),
  });

  if (query.isLoading) {
    return <div className='py-8 text-sm text-muted-foreground'>Loading RFC…</div>;
  }

  if (!account || !details) {
    return (
      <div className='flex justify-center py-16'>
        <div className='w-full max-w-[560px] rounded-[14px] border border-[#E4E8EE] bg-white px-8 py-10 text-center shadow-sm'>
          <div className='mx-auto mb-5 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-[#DBEAFE]'>
            <FileText size={28} className='text-[#3B82F6]' />
          </div>
          <h3 className='mb-2 text-[20px] font-bold text-[#0F172A]'>No RFC created yet</h3>
          <p className='mx-auto mb-7 max-w-sm text-[14px] text-[#64748B]'>
            Generate the Request for Coverage to get an access link and password you can share with
            the submitter.
          </p>
          <Button onClick={() => startRfc.mutate({accountsId})} disabled={startRfc.isPending}>
            {startRfc.isPending ? 'Generating…' : 'Generate RFC'}
          </Button>
        </div>
      </div>
    );
  }

  // Review gating and the edit lock stay keyed by the legacy RfcSubmission
  // status — they guard legacy clients.rfc mutations whose preconditions check
  // it. Only the visual status (banner + pill) follows the account.
  const status = details.status as RfcStatus;
  const meta = accountStatusMeta(config, account, account.isArchived, account.archiveReason);
  const reviewPending =
    approveBrokerStep.isPending || brokerReject.isPending || adjudicate.isPending;

  // A broker reviews the submission once it has been routed to them; a client
  // admin reviews it when it lands in their queue (directly, or after the broker
  // has approved). Internal admins are served by the legacy deal review page.
  const brokerCanReview = isBrokerUser && status === RfcStatus.BROKER_REVIEW;
  const clientCanReview =
    !isBrokerUser &&
    isClientAdmin &&
    (status === RfcStatus.SUBMITTED ||
      status === RfcStatus.CLIENT_REVIEW ||
      status === RfcStatus.BROKER_APPROVED);
  // Client admins may complete the broker-review step on the broker's behalf
  // (e.g. an unresponsive broker). Approve-only — rejection stays with the
  // broker. Server enforces the same `workflow.approve_rfc` key on
  // `rfc.approveBrokerStep` for client-profile callers.
  const clientCanApproveForBroker =
    !isBrokerUser && isClientAdmin && status === RfcStatus.BROKER_REVIEW;
  const canReview = brokerCanReview || clientCanReview || clientCanApproveForBroker;

  // Inline editing reuses the wizard's per-step forms (RfcStepForm) and saves
  // the whole form via clients.rfc.updateFormAdmin — same mutation the
  // deal/prospect AdminRfcEditor uses. For client users the server enforces
  // `rfc.edit` only (not `workflow.approve_rfc`), so mirror that here rather
  // than hiding the pencils from rfc.edit holders who can't approve.
  // Locked once the RFC reaches a terminal state.
  const isRfcLocked = status === RfcStatus.COMPLETED || status === RfcStatus.ARCHIVED;
  const canEdit =
    (!!user?.clientProfile || isBrokerUser || isInternalUser) && canEditRfcForm && !isRfcLocked;

  const cancelEdit = (): void => {
    resetForm();
    setEditingKey(null);
  };

  const saveEdit = async (): Promise<void> => {
    // Validate against the draft schema (same resolver the wizard uses) before
    // saving; abort and surface field errors if anything is invalid. The toast
    // covers errors on fields the section being edited doesn't render (the
    // whole wizard form validates at once), which would otherwise fail silently.
    const valid = await form.trigger();
    if (!valid) {
      toast({
        title: 'Validation error',
        description: 'Some fields are invalid. Fix the highlighted fields and try again.',
      });
      return;
    }

    const values = form.getValues();
    try {
      const response = await updateFormAdmin.mutateAsync({
        rfcId: details.id,
        formData: {
          section125FormData: values.section125Form,
          mecFormData: values.mecForm,
          clientPortalAccessFormData: values.clientPortalAccessForm,
        },
      });
      if (!response) return;
      toast({title: 'Success', description: 'The RFC was updated successfully.'});
      setEditingKey(null);
      utils.account.getById.invalidate({accountsId});
      utils.clients.rfc.getById.invalidate({rfcId: details.id});
    } catch {
      // Failures are surfaced by updateFormAdmin.onError
      // (createMutationErrorHandler); swallow the rejection so it doesn't bubble
      // out of the click handler as an unhandled promise rejection.
    }
  };

  const handleApprove = (): void => {
    // From BROKER_REVIEW the approval drives the broker-step mutation — for
    // the broker themselves, or a client admin acting on their behalf.
    if (brokerCanReview || clientCanApproveForBroker) {
      approveBrokerStep.mutate({rfcId: details.id, notes: reviewNote.trim() || undefined});
    } else {
      adjudicate.mutate({rfcId: details.id, action: 'APPROVE', notes: reviewNote.trim()});
    }
  };

  const handleReject = (): void => {
    const note = reviewNote.trim();
    if (!note) {
      toast({title: 'A reason is required to reject'});
      return;
    }
    if (brokerCanReview) {
      brokerReject.mutate({rfcId: details.id, notes: note});
    } else {
      adjudicate.mutate({rfcId: details.id, action: 'REJECT', notes: note});
    }
  };

  const accessUrl = `${window.location.origin}/rfc/${details.accessToken}`;
  const handleCopyLink = async (): Promise<void> => {
    await navigator.clipboard.writeText(accessUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const toggleSection = (key: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const jumpToSection = (key: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    document
      .getElementById(`rfc-section-${key}`)
      ?.scrollIntoView({behavior: 'smooth', block: 'start'});
  };

  // Agent of Record falls back to the account's assigned broker when the RFC's
  // own agent section is blank.
  const assignedBroker = query.data?.assignedBroker;
  const fallbackAgent = assignedBroker ? buildAgentOfRecordFromBroker(assignedBroker) : null;

  // Build the visible section list from whichever programs are enabled.
  const sections: SectionDescriptor[] = [
    ...(details.section125FormEnabled && details.section125FormData
      ? programSections(
          details.section125FormData as RfcSection125Form,
          '125',
          fallbackAgent,
          assignedBroker ?? null,
        )
      : []),
    ...(details.mecFormEnabled && details.mecFormData
      ? programSections(
          details.mecFormData as RfcMecForm,
          'mec',
          fallbackAgent,
          assignedBroker ?? null,
        )
      : []),
  ];

  const portalAccessUsers =
    (query.data?.clientPortalAccessEnabled &&
      (details.clientPortalAccessData as {users?: RfcContact[]} | null)?.users) ||
    [];
  if (portalAccessUsers.length > 0) {
    sections.push({
      key: 'portal-access',
      title: 'Client Portal Access',
      shortLabel: 'Portal Access',
      icon: <Users size={18} className='text-[#3B82F6]' />,
      iconBg: '#DBEAFE',
      complete: true,
      step: RfcStep.CLIENT_PORTAL_ACCESS,
      body: (
        <>
          {portalAccessUsers.map((u, i) => (
            <ContactBlock key={i} label={`User ${i + 1}`} contact={u} />
          ))}
        </>
      ),
    });
  }

  const completeCount = sections.filter((s) => s.complete).length;
  const pct = sections.length > 0 ? Math.round((completeCount / sections.length) * 100) : 0;

  const submittedOn = details.submittedAt
    ? new Date(details.submittedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const attachments = (rfcDetail.data?.attachments ?? []).map((a) => ({
    ...a,
    fileType: a.fileType as RfcAttachmentType,
    createdAt: new Date(a.createdAt).toISOString(),
  }));
  const notes = rfcDetail.data?.notes ?? [];
  const history = (rfcDetail.data?.history ?? []).map((h) => ({
    ...h,
    action: h.action as RfcAction,
    createdAt: new Date(h.createdAt).toISOString(),
  }));

  const subTabs: {key: SubTab; label: string}[] = [
    {key: 'details', label: 'Submission Details'},
    {key: 'files', label: `Files (${attachments.length})`},
    {key: 'notes', label: `Notes (${notes.length})`},
    {key: 'history', label: 'History'},
  ];

  return (
    <div className='max-w-4xl pb-6'>
      {/* Status banner */}
      <div
        className='mb-5 flex items-center justify-between rounded-lg border px-5 py-3'
        style={{backgroundColor: meta.bg, borderColor: meta.bg}}
      >
        <div className='flex items-center gap-2'>
          <CheckCircle size={16} style={{color: meta.text}} />
          <p className='text-[14px] font-medium' style={{color: meta.text}}>
            {submittedOn ? `Submitted on ${submittedOn} · ${meta.label}` : meta.label}
          </p>
        </div>
      </div>

      {/* Header */}
      <div className='mb-4 flex items-start justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <FileText size={22} className='text-[#3B82F6]' />
          <div>
            <div className='flex items-center gap-2.5'>
              <h2 className='text-[22px] font-bold text-[#0F172A]'>Request for Coverage (RFC)</h2>
              <span
                className='inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold'
                style={{backgroundColor: meta.bg, color: meta.text}}
              >
                {meta.label}
              </span>
            </div>
            <p className='mt-1 text-[13px] text-[#64748B]'>
              {submittedOn ? `Submitted ${submittedOn} · ` : ''}
              {completeCount} of {sections.length} sections completed
            </p>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <RfcGeneratePdfButton
            variant='outline'
            size='sm'
            clientName={query.data?.companyName ?? 'Client'}
            section125FormEnabled={!!details.section125FormEnabled}
            mecFormEnabled={!!details.mecFormEnabled}
            section125Form={details.section125FormData as RfcSection125Form}
            mecForm={details.mecFormData as RfcMecForm}
            includeInternalFields={isInternalUser}
            showPepm={showPepm}
            clientPortalAccessForm={details.clientPortalAccessData as RfcClientPortalAccessForm}
            achEnabled={query.data?.achAuthorizationEnabled ?? false}
          />
          <Button variant='outline' size='sm' onClick={() => window.print()}>
            <Printer className='mr-1.5 h-4 w-4' /> Print
          </Button>
        </div>
      </div>

      {/* Sub-tab nav */}
      <div className='mb-5 flex items-center gap-1 border-b border-[#E4E8EE]'>
        {subTabs.map((t) => (
          <button
            key={t.key}
            type='button'
            onClick={() => setSubTab(t.key)}
            className={cn(
              'cursor-pointer border-b-2 px-4 py-2.5 text-[14px] font-medium transition',
              subTab === t.key
                ? 'border-[#3B82F6] text-[#0F172A]'
                : 'border-transparent text-[#64748B] hover:text-[#0F172A]',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'details' ? (
        <RfcContext.Provider
          value={{
            accessToken: '',
            password: '',
            section125FormEnabled: !!details.section125FormEnabled,
            mecFormEnabled: !!details.mecFormEnabled,
            attachments: (rfcDetail.data?.attachments ?? []).map((a) => ({
              id: a.id,
              fileName: a.fileName,
              fileSize: a.fileSize,
              fileType: a.fileType as RfcAttachmentType,
            })),
            refreshAttachments: () => rfcDetail.refetch(),
          }}
        >
          <FormProvider {...form}>
            {/* Progress bar */}
            <div className='mb-4 h-1 w-full overflow-hidden rounded-full bg-[#E4E8EE]'>
              <div className='h-full rounded-full bg-[#3B82F6]' style={{width: `${pct}%`}} />
            </div>

            {/* Completion strip */}
            {sections.length > 0 ? (
              <CompletionStrip sections={sections} onJump={jumpToSection} />
            ) : null}

            {/* Access link + password */}
            <AccessLinkPanel
              accessUrl={accessUrl}
              newPassword={newPassword}
              onCopyLink={handleCopyLink}
              copied={copied}
              onRegenerate={() => regeneratePassword.mutate({accountsId})}
              regenerating={regeneratePassword.isPending}
            />

            {/* Review actions */}
            {canReview ? (
              <div className='mb-5 rounded-[14px] border border-[#E4E8EE] bg-white p-6 shadow-sm'>
                <div className='mb-3 flex items-center gap-2'>
                  <ShieldCheck size={18} className='text-[#3B82F6]' />
                  <h3 className='text-[16px] font-bold text-[#0F172A]'>Review submission</h3>
                </div>
                <p className='mb-4 text-[14px] text-[#64748B]'>
                  {brokerCanReview
                    ? 'This RFC is awaiting your review. Approving forwards it to the client admin; rejecting sends it back.'
                    : clientCanApproveForBroker
                      ? "This RFC is awaiting broker review. As an admin, you can approve it on the broker's behalf to move it forward."
                      : 'This RFC is awaiting your review. Approve to advance it, or reject to send it back.'}
                </p>
                <div className='mb-4'>
                  <p className='mb-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-[#94A3B8]'>
                    Note {brokerCanReview ? '(required to reject)' : '(optional)'}
                  </p>
                  <Textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder='Add a note for this decision…'
                    rows={3}
                  />
                </div>
                <div className='flex gap-2'>
                  <Button onClick={handleApprove} disabled={reviewPending} className='flex-1'>
                    {reviewPending
                      ? 'Working…'
                      : clientCanApproveForBroker
                        ? "Approve on Broker's Behalf"
                        : 'Approve'}
                  </Button>
                  {/* Rejection from BROKER_REVIEW stays with the broker. */}
                  {!clientCanApproveForBroker && (
                    <Button
                      variant='outline'
                      onClick={handleReject}
                      disabled={reviewPending}
                      className='flex-1 text-red-600 hover:text-red-700'
                    >
                      Reject
                    </Button>
                  )}
                </div>
              </div>
            ) : null}

            {/* Collapsible section cards */}
            {sections.length > 0 ? (
              sections.map((section) => (
                <SectionCard
                  key={section.key}
                  section={section}
                  collapsed={collapsed.has(section.key)}
                  onToggle={() => toggleSection(section.key)}
                  canEdit={canEdit && editingKey === null}
                  editing={editingKey === section.key}
                  onEdit={() => {
                    setEditingKey(section.key);
                    setCollapsed((prev) => {
                      const next = new Set(prev);
                      next.delete(section.key);
                      return next;
                    });
                  }}
                  onSave={saveEdit}
                  onCancel={cancelEdit}
                  saving={updateFormAdmin.isPending}
                  showPepm={showPepm}
                />
              ))
            ) : (
              <div className='rounded-[14px] border border-[#E4E8EE] bg-white px-6 py-10 text-center text-[14px] text-[#94A3B8] shadow-sm'>
                The RFC form hasn&apos;t been filled out yet.
              </div>
            )}
          </FormProvider>
        </RfcContext.Provider>
      ) : null}

      {subTab === 'files' ? (
        <RfcFileList
          attachments={attachments}
          showDownloadLinks
          refreshList={() => {
            if (rfcId) utils.clients.rfc.getById.invalidate({rfcId});
          }}
        />
      ) : null}

      {subTab === 'notes' ? (
        notes.length === 0 ? (
          <div className='rounded-[14px] border border-[#E4E8EE] bg-white px-6 py-10 text-center shadow-sm'>
            <MessageSquare size={28} className='mx-auto mb-3 text-[#94A3B8]' />
            <p className='text-[14px] text-[#94A3B8]'>No notes yet.</p>
          </div>
        ) : (
          <div className='space-y-3'>
            {notes.map((note) => (
              <div
                key={note.id}
                className='rounded-[14px] border border-[#E4E8EE] bg-white p-5 shadow-sm'
              >
                <div className='mb-1.5 flex items-center justify-between'>
                  <p className='text-[13px] font-semibold text-[#0F172A]'>
                    {note.createdByUser?.name ?? 'Unknown'}
                  </p>
                  <p className='text-[12px] text-[#94A3B8]'>
                    {new Date(note.createdAt).toLocaleString()}
                  </p>
                </div>
                <p className='whitespace-pre-wrap text-[14px] text-[#64748B]'>{note.content}</p>
              </div>
            ))}
          </div>
        )
      ) : null}

      {subTab === 'history' ? <RfcHistory history={history} /> : null}
    </div>
  );
}
